import React, { useRef, useEffect, useState } from 'react';
import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import { LoaderCircleIcon } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { ReactComponent as SearchIcon } from '../../../icons/svg/search.svg';
import { ReactComponent as CloseIcon } from '../../../icons/svg/close.svg';
import { Button as RoccoButton } from '../../../components/button';
import { useClickAway, useInViewport } from 'ahooks';
import type {
    SearchSuggestion,
    SearchSuggestionGroup,
} from '../view-models/search-suggestion';
import { SearchSuggestionPopup } from './SearchSuggestionPopup';
import type { DisplayMode } from '../../../types/enum/display-mode';
import { SearchRecentlySearchedPopup } from './SearchRecentlySearchedPopup';
import { uiStateAtomSearchClicked } from '../../website-header/states/ui-states-search-clicked';
import { useAtom } from 'jotai';

export interface SearchBarProps extends VariantProps<
    typeof _searchBarVariants
> {
    placeholderTexts: string[];
    rotationInterval?: number;
    suggestionGroups?: SearchSuggestionGroup[];
    value: string;
    isSearching?: boolean;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onSearchSubmit: (
        value: string | SearchSuggestion,
        isCategory: boolean,
    ) => void;
    /**
     * Called when a suggestion is clicked. String values are keyword suggestions
     * (should trigger a search). Object values are category/professional suggestions
     * (should navigate to suggestion.link, not be used as a search term).
     */
    onSearchSuggestionClick?: (suggestion: SearchSuggestion | string) => void;
    clearSearchQuery: () => void;
    hideClearButton?: boolean;
    hideSubmitButton?: boolean;
    displayMode: DisplayMode;
    disableDropdown?: boolean;
    recentlySearched?: string[];
    clearHistory?: () => void;
    onRecentlySearchedClick?: (search: string) => void;
    menuColor?: 'light' | 'dark';
    productsPageTopnavDec2025Enabled?: boolean;
}

const _searchBarVariants = cva(
    'relative flex items-center justify-between overflow-hidden rounded-full',
    {
        variants: {
            variant: {
                transparent: 'bg-transparent',
                searchPageMobile:
                    'h-[58px] border border-[#F2f2f2] bg-[#F7f7f7] p-5',
                default: 'h-16.5 bg-white pr-2 pl-4 shadow-island',
                header: 'h-16.5 rounded-full bg-black/20 px-4 text-xl backdrop-blur-xl',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
);

const _searchBarInputVariants = cva(
    'text-input-size w-full bg-transparent placeholder:text-ellipsis placeholder:text-gray-700 [&::-webkit-search-cancel-button]:hidden',
    {
        variants: {
            variant: {
                transparent: 'pl-6 outline-hidden md:pl-4',
                searchPageMobile: 'text-[16px] outline-hidden',
                default: 'py-3 pr-0 pl-2 text-base outline-hidden',
                header: 'py-3 pr-0 pl-2 text-5 outline-hidden',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
);

export const SearchBar = React.forwardRef<HTMLInputElement, SearchBarProps>(
    (
        {
            placeholderTexts,
            rotationInterval = 3000,
            suggestionGroups,
            value,
            isSearching = false,
            onChange,
            onKeyDown,
            onSearchSubmit,
            clearSearchQuery,
            variant,
            hideClearButton = false,
            hideSubmitButton = false,
            disableDropdown = false,
            displayMode,
            onSearchSuggestionClick,
            recentlySearched,
            clearHistory,
            onRecentlySearchedClick,
            menuColor = 'dark',
            productsPageTopnavDec2025Enabled,
        },
        forwardedRef,
    ) => {
        const [currentPlaceholderIndex, setCurrentPlaceholderIndex] =
            useState(0);
        const [shouldShowDropdown, setShouldShowDropdown] = useState(false);
        const containerRef = useRef<HTMLDivElement>(null);
        const [isVisible] = useInViewport(containerRef);
        const [isFocused, setIsFocused] = useState(false);
        const [hasUserInteracted, setHasUserInteracted] = useState(false);

        // this state is used to fight the race condition between the input blur and the dropdown visibility
        // we need to pause the blur event for 200ms to make sure the dropdown is visible and the click event is fired
        const [shouldBlur, setShouldBlur] = useState(false);
        const blurTimeoutRef = useRef<NodeJS.Timeout>(undefined);

        // Hides the dropdown when the user scrolls (header variant only, where the
        // search bar is part of the sticky header and the dropdown should dismiss on page scroll).
        // Disabled for other variants because a tall suggestions list may itself require scrolling.
        useEffect(() => {
            if (variant !== 'header') {
                return;
            }

            const handleScroll = () => {
                setShouldShowDropdown(false);
            };

            window.addEventListener('scroll', handleScroll, { passive: true });
            return () => window.removeEventListener('scroll', handleScroll);
        }, [variant]);

        const hasValue = value.length > 0;
        const filteredSuggestionGroups =
            suggestionGroups?.filter((group) => group.options.length > 0) ?? [];

        const hasSuggestions = filteredSuggestionGroups.length > 0;

        const localInputRef = useRef<HTMLInputElement>(null);
        const inputRef = (forwardedRef ||
            localInputRef) as React.RefObject<HTMLInputElement>;

        const [searchIconClicked] = useAtom(uiStateAtomSearchClicked);
        const clearingQuery = useRef(false);

        useEffect(() => {
            if (value.length === 0 && isVisible) {
                const interval = setInterval(() => {
                    setCurrentPlaceholderIndex(
                        (prevIndex) =>
                            (prevIndex + 1) % placeholderTexts.length,
                    );
                }, rotationInterval);

                return () => clearInterval(interval);
            }
        }, [
            placeholderTexts.length,
            rotationInterval,
            value.length,
            isVisible,
        ]);

        useClickAway(() => {
            setShouldShowDropdown(false);
            clearingQuery.current = false;
        }, containerRef);

        const handleInputFocus = () => {
            setIsFocused(true);
            setShouldBlur(false);
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
            }

            // Use a small timeout to ensure this runs after any useClickAway effects
            setTimeout(() => {
                // Only show dropdown if user has actively interacted with the component
                if (hasUserInteracted && variant !== 'header') {
                    // For desktop, always show dropdown on focus to enable recently searched functionality
                    // For mobile, we may want different behavior based on searchIconClicked
                    if (displayMode === 'desktop' && !clearingQuery.current) {
                        setShouldShowDropdown(true);
                    } else {
                        // this is to prevent the dropdown from showing when we first landed on the search page
                        // but we still want to retain the auto focus on the input
                        if (!searchIconClicked && !clearingQuery.current) {
                            setShouldShowDropdown(true);
                        }
                    }
                }
                clearingQuery.current = false;
            }, 0);
        };

        const handleInputBlur = () => {
            setShouldBlur(true);
            clearingQuery.current = false;
            blurTimeoutRef.current = setTimeout(() => {
                if (shouldBlur) {
                    setIsFocused(false);
                }
            }, 200);
        };

        useEffect(() => {
            return () => {
                if (blurTimeoutRef.current) {
                    clearTimeout(blurTimeoutRef.current);
                }
            };
        }, []);

        const handleSubmit = (e: React.FormEvent) => {
            e.preventDefault();
            onSearchSubmit(value, false);
            setShouldShowDropdown(false);
        };

        const handleClearQuery = () => {
            clearingQuery.current = true;
            clearSearchQuery();
            // After clearing, ensure input stays focused and dropdown shows
            inputRef.current?.focus();
        };

        const handleSuggestionClick = (
            suggestion: SearchSuggestion | string,
        ) => {
            onSearchSuggestionClick?.(suggestion);
            setShouldShowDropdown(false);
            // Delay reset to prevent bubbling click events from re-enabling
            setTimeout(() => {
                setHasUserInteracted(false);
            }, 50);
        };

        const handleRecentSearchClick = (search: string) => {
            onRecentlySearchedClick?.(search);
            setShouldShowDropdown(false);
            // Delay reset to prevent bubbling click events from re-enabling
            setTimeout(() => {
                setHasUserInteracted(false);
            }, 50);
        };

        const handleUserInteraction = () => {
            setHasUserInteracted(true);
            clearingQuery.current = false;
            if (displayMode === 'desktop' && !clearingQuery.current) {
                setShouldShowDropdown(true);
            }
        };

        const canShowSuggestion =
            hasValue &&
            hasSuggestions &&
            onSearchSuggestionClick &&
            isFocused &&
            hasUserInteracted;
        const canShowRecentlySearched =
            !hasValue &&
            recentlySearched &&
            recentlySearched.length > 0 &&
            clearHistory &&
            onRecentlySearchedClick &&
            isFocused &&
            hasUserInteracted;
        const popupMode = canShowSuggestion
            ? 'suggestion'
            : canShowRecentlySearched
              ? 'recentlySearched'
              : 'none';

        const popupVariant: 'default' | 'header' =
            variant === 'header' ? 'header' : 'default';

        return (
            <div
                ref={containerRef}
                className="relative mx-auto w-full bg-transparent"
                onClick={() => {
                    setHasUserInteracted(true);
                    inputRef.current?.focus();
                }}
            >
                <form
                    onSubmit={handleSubmit}
                    role="search"
                    suppressHydrationWarning
                >
                    <div
                        className={cn(
                            _searchBarVariants({ variant }),
                            variant === 'header' &&
                                menuColor === 'dark' &&
                                (productsPageTopnavDec2025Enabled
                                    ? 'h-14.5 bg-[#bebebe]/20 backdrop-blur-xl'
                                    : 'bg-gray-100/40 backdrop-blur-xl'),
                        )}
                    >
                        {variant === 'searchPageMobile' && (
                            <div className="mr-2.5">
                                <SearchIcon className="size-4 stroke-gray-700" />
                            </div>
                        )}
                        {variant === 'default' && (
                            <div className="mr-2 ml-2">
                                <SearchIcon className="size-5 stroke-gray-500" />
                            </div>
                        )}
                        {variant === 'header' && (
                            <div className="mr-2 ml-2">
                                <SearchIcon
                                    className={
                                        menuColor === 'light'
                                            ? 'size-5 stroke-white'
                                            : 'size-5 stroke-gray-500'
                                    }
                                />
                            </div>
                        )}
                        <input
                            ref={inputRef}
                            type="search"
                            enterKeyHint="search"
                            value={value}
                            onChange={(e) => {
                                setHasUserInteracted(true);
                                onChange(e);
                            }}
                            onKeyDown={(e) => {
                                setHasUserInteracted(true);
                                if (e.key === 'Enter') {
                                    setShouldShowDropdown(false);
                                } else {
                                    setShouldShowDropdown(true);
                                }
                                onKeyDown?.(e);
                            }}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            onClick={handleUserInteraction}
                            placeholder={
                                placeholderTexts[currentPlaceholderIndex]
                            }
                            className={cn(
                                _searchBarInputVariants({ variant }),
                                variant === 'header' && menuColor === 'light'
                                    ? 'text-white placeholder:text-[#e4e4e4]'
                                    : variant === 'header'
                                      ? 'text-gray-700 placeholder:text-[#323232] placeholder:opacity-40'
                                      : 'text-gray-700 placeholder:text-gray-400 placeholder:opacity-40',
                            )}
                            aria-autocomplete="list"
                            autoComplete={'off'}
                            aria-controls="search-autocomplete"
                            name="search"
                            tabIndex={0}
                        />

                        <div
                            className={cn(
                                'flex items-center gap-x-2',
                                variant === 'header' && 'relative -right-2',
                            )}
                        >
                            {!hideClearButton && hasValue ? (
                                <RoccoButton
                                    type="button"
                                    color={
                                        variant === 'header'
                                            ? 'header-search-button'
                                            : 'secondary'
                                    }
                                    size={
                                        variant === 'searchPageMobile'
                                            ? 'square18'
                                            : variant === 'header'
                                              ? productsPageTopnavDec2025Enabled
                                                  ? 'headerSearchSmall'
                                                  : 'headerSearch'
                                              : variant === 'default'
                                                ? 'headerSearch'
                                                : displayMode === 'mobile'
                                                  ? 'square50'
                                                  : 'square72'
                                    }
                                    onClick={handleClearQuery}
                                    aria-label="Clear Search Query"
                                    {...(variant === 'searchPageMobile' && {
                                        color: 'transparent',
                                    })}
                                >
                                    <CloseIcon className="size-5" />
                                </RoccoButton>
                            ) : null}

                            {!hideSubmitButton && (
                                <RoccoButton
                                    type="submit"
                                    color={
                                        variant === 'header'
                                            ? 'header-search-button'
                                            : 'dark'
                                    }
                                    size={
                                        variant === 'header'
                                            ? productsPageTopnavDec2025Enabled
                                                ? 'headerSearchSmall'
                                                : 'headerSearch'
                                            : variant === 'default'
                                              ? 'headerSearch'
                                              : displayMode === 'mobile'
                                                ? 'square50'
                                                : 'square72'
                                    }
                                    aria-label="Submit Search"
                                    style={{
                                        opacity:
                                            variant === 'transparent' &&
                                            displayMode === 'desktop' &&
                                            !isFocused
                                                ? 0.8
                                                : 1,
                                    }}
                                >
                                    {isSearching ? (
                                        <LoaderCircleIcon
                                            className={cn(
                                                'size-5 animate-spin',
                                                variant === 'header'
                                                    ? 'text-[#323232]'
                                                    : 'text-white',
                                            )}
                                        />
                                    ) : (
                                        <SearchIcon
                                            className={cn(
                                                'size-5',
                                                variant === 'header'
                                                    ? 'stroke-[#323232]'
                                                    : 'stroke-white',
                                            )}
                                        />
                                    )}
                                </RoccoButton>
                            )}
                        </div>
                    </div>
                </form>

                {!disableDropdown && shouldShowDropdown && (
                    <>
                        {popupMode === 'suggestion' ? (
                            <SearchSuggestionPopup
                                suggestionGroups={filteredSuggestionGroups}
                                query={value}
                                variant={popupVariant}
                                menuColor={menuColor}
                                onSearchSuggestionClick={handleSuggestionClick}
                            />
                        ) : popupMode === 'recentlySearched' ? (
                            <SearchRecentlySearchedPopup
                                recentlySearched={recentlySearched ?? []}
                                clearHistory={clearHistory ?? (() => {})}
                                variant={popupVariant}
                                menuColor={menuColor}
                                onItemClick={handleRecentSearchClick}
                            />
                        ) : null}
                    </>
                )}
            </div>
        );
    },
);

SearchBar.displayName = 'SearchBar';
